import {
	GRID_CHECKBOX_SELECTION_COL_DEF,
	type GridColDef,
	type GridValidRowModel
} from "@mui/x-data-grid-pro";

export type GridCsvBlobOptions = {
	delimiter?: string;
	includeHeaders?: boolean;
	utf8WithBom?: boolean;
};

function escapeCsvCell(value: string, delimiter: string): string {
	const escaped = value.replace(/"/g, '""');
	if ([delimiter, "\n", "\r"].some((d) => escaped.includes(d))) {
		return `"${escaped}"`;
	}
	return escaped;
}

function cellValueForExport<R extends GridValidRowModel>(
	col: GridColDef<R>,
	row: R
): string {
	if (!col.field || col.field === GRID_CHECKBOX_SELECTION_COL_DEF.field || col.disableExport) {
		return "";
	}
	const raw = (row as unknown as Record<string, unknown>)[col.field];
	let value: unknown = raw;
	if (col.valueGetter) {
		// MUI valueGetter expects full cell params; typical column defs only use id/field/row/value.
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- partial params for export only
		value = (col.valueGetter as any)({id: (row as {id?: unknown}).id, field: col.field, row, value: raw});
	}
	if (col.valueFormatter) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- partial params for export only
		return String((col.valueFormatter as any)({value, id: (row as {id?: unknown}).id, field: col.field, row}) ?? "");
	}
	if (value == null) {
		return "";
	}
	if (typeof value === "object") {
		return JSON.stringify(value);
	}
	return String(value);
}

/**
 * Build a CSV blob from row models and visible column definitions (same formatting rules as MUI export).
 */
export function buildGridCsvBlob<R extends GridValidRowModel>(
	rows: R[],
	visibleColumns: GridColDef<R>[],
	options?: GridCsvBlobOptions
): Blob {
	const delimiter = options?.delimiter ?? ",";
	const includeHeaders = options?.includeHeaders ?? true;
	const utf8WithBom = options?.utf8WithBom ?? true;
	const dataCols = visibleColumns.filter(
		(c) =>
			c.field &&
			c.field !== GRID_CHECKBOX_SELECTION_COL_DEF.field &&
			!c.disableExport
	);
	const lines: string[] = [];
	if (includeHeaders) {
		lines.push(
			dataCols.map((c) => escapeCsvCell(String(c.headerName ?? c.field), delimiter)).join(delimiter)
		);
	}
	for (const row of rows) {
		lines.push(
			dataCols.map((c) => escapeCsvCell(cellValueForExport(c, row), delimiter)).join(delimiter)
		);
	}
	const body = lines.join("\r\n");
	const parts: BlobPart[] = [];
	if (utf8WithBom) {
		parts.push(new Uint8Array([0xef, 0xbb, 0xbf]));
	}
	parts.push(body);
	return new Blob(parts, {type: "text/csv"});
}

export type DownloadCsvBlobOptions = {
	/** Used when `fileBaseName` is empty after sanitization */
	defaultBaseName?: string;
};

/**
 * Trigger a browser download for a CSV blob. Sanitizes the base name for a safe filename.
 */
export function downloadCsvBlob(
	blob: Blob,
	fileBaseName: string,
	options?: DownloadCsvBlobOptions
): void {
	const fallback = options?.defaultBaseName ?? "export";
	const safe =
		fileBaseName.replace(/[^\w-]+/g, "_").replace(/^_+|_+$/g, "").trim() || fallback;
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = `${safe}.csv`;
	a.click();
	URL.revokeObjectURL(url);
}

/**
 * Dedupe rows by `id` (first wins). Rows without `id` are appended as-is (use `dedupeSelection: false` if undesired).
 */
export function dedupeGridRowsById<R extends GridValidRowModel>(rows: R[]): R[] {
	const seen = new Set<unknown>();
	const out: R[] = [];
	for (const row of rows) {
		const id = (row as {id?: unknown}).id;
		if (id === undefined || id === null) {
			out.push(row);
			continue;
		}
		if (seen.has(id)) {
			continue;
		}
		seen.add(id);
		out.push(row);
	}
	return out;
}
